import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, BookOpenCheckIcon, ClipboardCheckIcon, Clock3Icon, SparklesIcon, TargetIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getCheckoutUrl, getStudyUrl } from '../../../utils/studyAccess';
import { useStudyMembership } from '../../../utils/useStudyMembership';
const PROGRAM_ID = 'rd-ready-rd-exam' as const;
const studyFeatures = [{
  icon: TargetIcon,
  title: 'Focused review',
  description: 'Study the nutrition care concepts most relevant to your RD exam preparation.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Practice with purpose',
  description: 'Build confidence through clear, structured learning moments at your own pace.'
}, {
  icon: Clock3Icon,
  title: 'Study on your schedule',
  description: 'Return to your purchased program whenever it works for you.'
}];
export function RDExam() {
  const membership = useStudyMembership(PROGRAM_ID);
  const hasAccess = membership.hasAccess;
  return <div className="min-h-screen w-full bg-amber-50 text-slate-900" data-id="element-2499" data-mp="39qbkvgxp2zix">
      <section className="overflow-hidden border-b border-amber-200 bg-[#fff7dc]" data-id="element-2500" data-mp="3esaq6wczw6ii">
        <div className="mx-auto grid max-w-6xl gap-12 px-5 py-16 sm:px-8 lg:grid-cols-[1.08fr_0.92fr] lg:items-center lg:gap-16 lg:py-24" data-id="element-2501" data-mp="3jqc9niec27ui">
          <motion.div initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.45
        }} data-id="element-2502" data-mp="3ojf37qj41hap">
            <p className="mb-5 inline-flex items-center gap-2 rounded-full bg-[#ffe36e] px-4 py-2 text-sm font-bold text-slate-900" data-id="element-2503" data-mp="6d0q1tso1bdd">
              <SparklesIcon className="h-4 w-4" aria-hidden="true" data-id="element-2504" data-mp="b8g2qiext187" />
              RD Ready study space
            </p>
            <h1 className="max-w-2xl text-5xl font-black tracking-[-0.05em] text-slate-900 sm:text-6xl" data-id="element-2505" data-mp="2d1vtrgexsi3p">
              RD Exam Prep,
              <span className="block text-[#ec5e3c]" data-id="element-2506" data-mp="252ir94kt83uh">
                built to help you pass.
              </span>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-8 text-slate-700" data-id="element-2507" data-mp="2047lb4xmydhj">
              A focused study system for Registered Dietitian exam preparation.
              Unlock your account-bound program for a one-time purchase of $150.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row" data-id="element-2508" data-mp="2nzjzcy9nuk2n">
              <Link to={hasAccess ? getStudyUrl(PROGRAM_ID) : getCheckoutUrl(PROGRAM_ID)} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#ec5e3c] px-6 py-3.5 text-base font-bold text-white transition hover:bg-[#ce4d2f] focus:outline-none focus:ring-4 focus:ring-[#ec5e3c]/30" data-id="element-2509" data-mp="2j18p4mfge7jw">
                {hasAccess ? 'Open study' : 'Unlock RD Prep · $150'}
                <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-2510" data-mp="32nevkomvn6kh" />
              </Link>
              {!hasAccess && <Link to="/login" state={{
              from: getCheckoutUrl(PROGRAM_ID)
            }} className="inline-flex items-center justify-center rounded-full border-2 border-slate-900 px-6 py-3 text-base font-bold text-slate-900 transition hover:bg-slate-900 hover:text-white focus:outline-none focus:ring-4 focus:ring-slate-900/20" data-id="element-2511" data-mp="3qd4uk8bhoclk">
                  Sign in to purchase
                </Link>}
            </div>
            <p className="mt-4 text-sm font-medium text-slate-600" data-id="element-2512" data-mp="2tndtfq8ozr6s">
              One-time $150 purchase · access stays with your RD Ready account.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5,
          delay: 0.08
        }} className="relative rounded-[2rem] border-4 border-slate-900 bg-[#6ed7c4] p-6 shadow-[8px_8px_0_#172033] sm:p-8" data-id="element-2513" data-mp="2jnwmz6lm999r">
            <div className="rounded-[1.4rem] border-2 border-slate-900 bg-white p-6" data-id="element-2514" data-mp="3i6j0a8g91zfl">
              <div className="flex items-center justify-between" data-id="element-2515" data-mp="3d8r8rfjiofbx">
                <BookOpenCheckIcon className="h-9 w-9 text-[#ec5e3c]" aria-hidden="true" data-id="element-2516" data-mp="38apgc407b3ns" />
                <span className="rounded-full bg-[#fff0a6] px-3 py-1 text-xs font-extrabold uppercase tracking-wider" data-id="element-2517" data-mp="339rvtkrpxxj4">
                  RD exam
                </span>
              </div>
              <p className="mt-10 text-sm font-bold uppercase tracking-[0.16em] text-slate-500" data-id="element-2518" data-mp="2ubzgwhpwsrw9">
                Today’s focus
              </p>
              <p className="mt-2 text-3xl font-black tracking-tight" data-id="element-2519" data-mp="sj1sqz72unhn">
                Make your study plan feel doable.
              </p>
              <div className="mt-8 h-3 rounded-full bg-slate-100" data-id="element-2520" data-mp="101ja1rl736l6">
                <div className="h-3 w-2/3 rounded-full bg-[#ec5e3c]" data-id="element-2521" data-mp="151e0ju125u5p" />
              </div>
              <p className="mt-3 text-sm font-semibold text-slate-600" data-id="element-2522" data-mp="19sdsekvblm1c">
                Quizzes, flashcards, and focused progress tools.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-16 sm:px-8 lg:py-24" data-id="element-2523" data-mp="h4i0ruklykbf">
        <div className="max-w-2xl" data-id="element-2524" data-mp="m2rz2e1mq119">
          <p className="text-sm font-extrabold uppercase tracking-[0.16em] text-[#0b8776]" data-id="element-2525" data-mp="m3ukw7k3hdab">
            Built for momentum
          </p>
          <h2 className="mt-3 text-3xl font-black tracking-[-0.04em] sm:text-4xl" data-id="element-2526" data-mp="r3z445ce0m48">
            A study space that meets you where you are.
          </h2>
        </div>
        <div className="mt-10 grid gap-5 md:grid-cols-3" data-id="element-2527" data-mp="22g9opogz9bxl">
          {studyFeatures.map(({
          icon: Icon,
          title,
          description
        }) => <article key={title} className="rounded-3xl border-2 border-slate-900 bg-white p-6 shadow-[4px_4px_0_#172033]" data-id="element-2528" data-mp="26kpu7omoihca">
              <Icon className="h-8 w-8 text-[#ec5e3c]" aria-hidden="true" data-id="element-2529" data-mp="2e0dxym7x5dl" />
              <h3 className="mt-5 text-xl font-black" data-id="element-2530" data-mp="gf31kfsv2nqm">{title}</h3>
              <p className="mt-2 leading-7 text-slate-600" data-id="element-2531" data-mp="gc72up96vv14">{description}</p>
            </article>)}
        </div>
      </section>

      <section className="bg-[#172033] px-5 py-16 text-white sm:px-8" data-id="element-2532" data-mp="3juiatetf0tap">
        <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-7 rounded-[2rem] border-2 border-white/30 bg-[#274458] p-8 sm:p-10 lg:flex-row lg:items-center" data-id="element-2533" data-mp="3ftoo2orplkje">
          <div data-id="element-2534" data-mp="3tmxssc4pphvl">
            <h2 className="text-3xl font-black tracking-[-0.04em]" data-id="element-2535" data-mp="3oumrp5y8jq1g">
              Ready to study with intention?
            </h2>
            <p className="mt-3 max-w-xl text-lg text-slate-200" data-id="element-2536" data-mp="313qsvovd3ukp">
              {hasAccess ? 'Your RD Exam Prep program is unlocked.' : 'Unlock your RD Exam Prep study space for a one-time $150 purchase.'}
            </p>
          </div>
          <Link to={hasAccess ? getStudyUrl(PROGRAM_ID) : getCheckoutUrl(PROGRAM_ID)} className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#ffe36e] px-6 py-3.5 font-bold text-slate-900 transition hover:bg-white focus:outline-none focus:ring-4 focus:ring-[#ffe36e]/40" data-id="element-2537" data-mp="1ge4wepos4a2r">
            {hasAccess ? 'Open study' : 'Unlock for $150'}
            <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-2538" data-mp="1zczf29rd5uo7" />
          </Link>
        </div>
      </section>
    </div>;
}